import { Injectable } from 'angular-ts-decorators';

import { filter } from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';

import { Initable } from '@shared/app/scripts/services/initable';
import { Domain } from '@shared/app/scripts/services/domain';
import { DateSource } from '@shared/app/scripts/services/date-source';
import { SettingsManager } from '@shared/app/scripts/services/settings-manager';
import type { LocalStorage } from '@shared/app/scripts/services/local-storage';

export interface IRetailSalesObject {
  articleCode: string;
  articleDescription: string;
  barcode: string;
  brand: string;
  category: string;
  color: string;
  group: string;
  qty: number;
  price: number;
  nettPrice: number;
  sizeLable: string;
  vat: number;
}

export interface ISkuObject {
  articleCode: string;
  articleCodeSupplier: string;
  articleDescription: string;
  articleGroup: string;
  barcode: string;
  brand: string;
  buyPrice: number;
  color: string;
  colorCode: string;
  colorCodeSupplier: string;
  colorDescription: string;
  colorFamily: string;
  colorSupplier: string;
  originalPrice: number;
  price: number;
  qty: number;
  size: string;
  sizeIndex: number;
  subColorDescription: string;
  suggestedRetailPrice: number;
  valuePrice: number;
  vat: number;
}

/**
 * Service to download the complete sku database of a tenant from AWS S3. The
 * data is stored locally in an in-memory array.
 */
@Injectable('dataFeedAws')
export class DataFeedAws implements Initable {
  private skusSubject = new BehaviorSubject<any>([]);
  private $translate: ng.translate.ITranslateService;

  public dependencies = ['settingsManager'];

  private wasInited = false;

  /**
   * Stream that emits everytime when new skus$ are loaded & remembers the last data
   */
  public skus$: Observable<any>;

  /**
   * Array of all loaded skus
   */
  public SKUS: ISkuObject[];

  constructor(
    private $http: ng.IHttpService,
    private localStorage: LocalStorage,
    private $q: ng.IQService,
    private domain: Domain,
    private moment,
    private settingsManager: SettingsManager,
    private dateSource: DateSource,
    private uuid,
    translate: ng.translate.ITranslateService,
  ) {
    'ngInject';

    this.skus$ = this.skusSubject.asObservable();
    this.$translate = translate;
  }

  /**
   * Downloads the data feed from the supplied data feed location
   * @returns {promise} resolves with nothing when all goes fine, fails with error message
   */
  init(): ng.IPromise<any> {
    if (this.wasInited) {
      return this.reload();
    } else {
      this.wasInited = true;

      return this.get()
        .then(() => undefined)
        .catch(() => {
          return this.$q.reject(
            this.$translate.instant('shared.datafeed.error_downloading_feed'),
          );
        });
    }
  }

  /**
   *
   * Get Request for the datafeed
   * @param {string} newToken cache busting token
   * @param {string} uploadDirectory the directory where the SKU Feed is stored
   *
   * @returns {Observable} returns an Observable
   */
  get(newToken?, uploadDirectory?): ng.IPromise<any> {
    const dataFeedDirectory = uploadDirectory
        ? uploadDirectory
        : this.settingsManager.settings.uploadDirectory,
      today = this.moment(this.dateSource.now()).format('YYYYMMDD'),
      baseUrl = `//${this.domain.createSibling('feeds')}/${dataFeedDirectory}`,
      token = newToken ? newToken : this.localStorage.getItem('dataFeedToken');

    if (angular.isUndefined(dataFeedDirectory)) {
      return this.$q.reject('uploadDirectory required');
    }

    let version = today;

    if (angular.isDefined(token)) {
      version += `-${token}`;
    }

    return this.$http
      .get(`${baseUrl}/SKUS.JS`, {
        params: {
          version,
        },
      })
      .then((response) => {
        if (!angular.isArray(response.data)) {
          return this.$q.reject(response.data);
        }

        // TODO LOG-65 as soon as logistics app will load data in right order
        // will be able to remove this undocumented property
        this.SKUS = response.data;

        // TODO rx anti-pattern, to be refactor
        this.skusSubject.next(this.SKUS);

        this.localStorage.setItem('dataFeedToken', token);

        return this.SKUS;
      });
  }

  /**
   *
   * Force a download of fresh data
   *
   * @returns {Observable} returns an Observable
   */
  reload(): ng.IPromise<any> {
    return this.get(this.uuid.v4())
      .then(() => undefined)
      .catch(() => {
        return this.$q.reject(
          this.$translate.instant('shared.datafeed.error_downloading_feed'),
        );
      });
  }

  /**
   *
   * Locates a SKU by it's barcode. This method returns a sku object for the
   * logistics transaction types.
   *
   * This function is similar to findBarcode, but the SKU in a logistics
   * context has additional properties that are not necessary in a retail sales context.
   *
   * # SKU Data Schema
   *
   * The {@link https://docs.dev.swcloud.nl/dev/index.html#/data/post_receivings_save
   * SKU schema} is part of the receivings schema.
   *
   * <pre>
   * {
   *   articleCode: 'ABC123',
   *   articleCodeSupplier: 'G-Star',
   *   articleDescription: 'A cool coat',
   *   articleGroup: 'coats',
   *   barcode: '1234',
   *   brand: 'My Brand',
   *   color: '001 red',
   *   colorCode: '001',
   *   colorDescription: 'red',
   *   colorSupplier: 'G-Star',
   *   qty: 0,
   *   price: 99.99,
   *   originalPrice: 99.99,
   *   suggestedRetailPrice: 150.00,
   *   buyPrice: 50.00,
   *   valuePrice: 75.00,
   *   size: 'L',
   *   vat: 21
   * }
   * </pre>
   * @param {string} barcode the barcode of the sku
   * @returns {object} returns a SKU object
   */
  findLogisticsSku(barcode: string): ISkuObject {
    let sku: any = filter(this.SKUS, { barcode });

    return sku.length ? angular.copy(sku[0]) : null;
  }

  /**
   *
   * Locates a sku by it's barcode. This method returns a barcode object for the retail sales object.
   *
   * # SKU Data Schema
   *
   * The barcode schema is part of the sales schema.
   *
   * <pre>
   * {
   *   articleCode: 'ABC123',
   *   articleDescription: 'A cool coat',
   *   barcode: '1234',
   *   brand: 'My Brand',
   *   category: 'barcode',
   *   color: 'red',
   *   group: 'some group',
   *   qty: 0,
   *   price: 99.99,
   *   nettPrice: 99.99,
   *   sizeLable: 'L',
   *   vat: 21
   * }
   * </pre>
   * @param {string} barcode the barcode of the sku
   * @returns {object} returns a SKU object
   */
  findBarcode(barcode: string): IRetailSalesObject {
    let sku: any = filter(this.SKUS, { barcode: barcode });

    if (sku.length) {
      return {
        articleCode: sku[0].articleCode,
        articleDescription: sku[0].articleDescription,
        barcode: barcode,
        brand: sku[0].brand,
        category: 'barcode',
        color: sku[0].colorCode,
        group: sku[0].articleGroup,
        qty: 0,
        price: sku[0].price,
        nettPrice: sku[0].originalPrice,
        sizeLable: sku[0].size,
        vat: sku[0].vat,
      } as IRetailSalesObject;
    } else {
      return null;
    }
  }
}
